import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Stethoscope, X, Check, FileText, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/seo";
import AdminTabs from "@/components/AdminTabs";

/** Admin-only: therapist verification queue + audit log. */
export default function AdminVerify() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [stats, setStats] = useState(null);
  const [audit, setAudit] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  useSEO({ title: "Admin · Verification queue", noindex: true });

  const reload = async () => {
    setError("");
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/therapists?status=pending"),
        api.get("/admin/stats"),
        api.get("/admin/audit-log?limit=30"),
      ]);
      setPending(a.data?.items || []);
      setStats(b.data || null);
      setAudit(c.data?.items || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { reload(); }, []);

  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/therapists/${id}/approve`, { reason: "" });
      await reload();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy("");
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (sent to the therapist):");
    if (!reason) return;
    setBusy(id);
    try {
      await api.post(`/admin/therapists/${id}/reject`, { reason });
      await reload();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy("");
  };

  if (user && user.role !== "admin") {
    return (
      <div className="min-h-screen dotted-bg flex items-center justify-center px-5">
        <div className="brutal-card bg-white p-6 max-w-md w-full">
          <ShieldCheck size={28} className="mb-3 text-[color:var(--coral-deep)]" />
          <h1 className="display text-2xl font-extrabold mb-2">Admins only</h1>
          <p className="text-[color:var(--ink-soft)] mb-4">
            This area is for SonSpeak admins. If you're trying to apply as a therapist, head to the public application form.
          </p>
          <Link to="/therapist-application" className="pill-btn primary">Therapist application</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen dotted-bg" data-testid="admin-verify-page">
      <header className="px-4 sm:px-6 py-3 border-b-[3px] border-[color:var(--ink)] bg-[color:var(--cream)] flex items-center gap-3">
        <Link to="/profiles" className="pill-btn"><ArrowLeft size={16} /> Back</Link>
        <div className="display text-lg sm:text-xl font-extrabold">Admin · verification queue</div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <AdminTabs current="verify" />
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-stats">
            <Stat label="Pending" value={stats.therapists_pending} highlight />
            <Stat label="Approved" value={stats.therapists_approved} />
            <Stat label="Family stories live" value={stats.family_profiles_published} />
            <Stat label="Audit actions today" value={stats.audit_actions_today} />
          </div>
        )}

        {error && (
          <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3 mb-4 flex items-start gap-2"><AlertCircle size={16} className="mt-0.5" /> {error}</div>
        )}

        <h2 className="display text-2xl font-extrabold mb-3">Pending therapist applications</h2>
        {pending.length === 0 ? (
          <div className="brutal-card bg-white p-5 text-[color:var(--ink-soft)] mb-8">No applications waiting. 🎉</div>
        ) : (
          <ul className="space-y-3 mb-10">
            {pending.map((t) => (
              <li key={t.id} className="brutal-card bg-white p-4" data-testid={`admin-pending-${t.id}`}>
                <div className="flex items-start gap-3 mb-2">
                  <Stethoscope size={20} className="text-[color:var(--coral-deep)] mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="display text-lg font-extrabold">{t.full_name}</div>
                    <div className="text-xs text-[color:var(--ink-soft)]">
                      {t.credentials} · ASHA {t.asha_number || "—"} · {t.license_state} #{t.state_license_number}
                    </div>
                    <div className="text-xs text-[color:var(--ink-soft)] mt-0.5">
                      {t.contact_email} {t.website ? `· ${t.website}` : ""}
                    </div>
                  </div>
                </div>
                <p className="text-sm leading-relaxed mb-3">{t.bio}</p>
                <div className="flex gap-2">
                  <button type="button" disabled={busy === t.id} onClick={() => approve(t.id)} className="pill-btn primary" data-testid={`admin-approve-${t.id}`}>
                    <Check size={14} /> Approve
                  </button>
                  <button type="button" disabled={busy === t.id} onClick={() => reject(t.id)} className="pill-btn danger" data-testid={`admin-reject-${t.id}`}>
                    <X size={14} /> Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <h2 className="display text-2xl font-extrabold mb-3 flex items-center gap-2">
          <FileText size={20} /> Recent admin activity
        </h2>
        <ul className="space-y-1 text-sm" data-testid="admin-audit-log">
          {audit.map((row, i) => (
            <li key={i} className="brutal-card bg-white p-2 flex flex-wrap gap-2 items-center">
              <span className="font-mono text-[10px] text-[color:var(--ink-soft)] shrink-0">{new Date(row.ts).toLocaleString()}</span>
              <span className="font-bold">{row.action}</span>
              <span className="text-[color:var(--ink-soft)]">{row.target_type}/{row.target_id?.slice(0,8)}</span>
              <span className="text-[color:var(--ink-soft)] ml-auto">{row.admin_email}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3">
          <Link to="/admin/audit-log" className="text-sm underline" data-testid="admin-audit-full">See full audit log →</Link>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div className={`brutal-card p-3 ${highlight ? "bg-[color:var(--gold)]" : "bg-white"}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[color:var(--ink-soft)]">{label}</div>
      <div className="display text-3xl font-extrabold leading-none mt-1">{value ?? "—"}</div>
    </div>
  );
}
